import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { Avatar, Spin, Timeline } from 'antd';
import dayjs from 'dayjs';
import { Icon } from '../Core/Icon';
import { useAuth } from '../../hooks/useAuth';
import { dateToTicks } from '../../utils/utils';
import { dateFromTicks } from '../../services/utils';
import { GetActivityLogsPayload } from '../../interfaces/ActivityLogService';
import ActivityLog from 'interfaces/ActivityLog';
import { ActivityLogService } from '../../services/activityLogService';
import File from 'interfaces/File';
interface FileDetailActivitiesTabProps {
  file: File;
}

const activityLogService = new ActivityLogService();
const SCROLL_THRESHOLD = 50;

const FileDetailActivitiesTab: React.FC<FileDetailActivitiesTabProps> = ({
  file,
}) => {
  const { onBehalfOfUser } = useAuth();
  const [filters, setFilters] = useState<GetActivityLogsPayload>({
    //userId: authenticatedUser?.userId,
    onBehalfOf: onBehalfOfUser?.userId,
    offset: 0,
    count: 25,
    fromDate: 0,
    toDate: dateToTicks(new Date()),
    detailsContains: '',
    //operationType: file.isFolder ? 'ReadFolder' : 'ReadFile',
    targetId: file.uid,
    targetType: file.isFolder ? 'Folder' : 'File',
  });
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const isFirstMount = useRef(true);

  /**
   * @description Fetch activities
   */
  const fetchActivities = useCallback(async () => {
    setIsLoading(true);
    try {
      const resp = await activityLogService.getActivityLogs(filters);
      const newLogs = resp.activityLogs;
      setActivities((prev) =>
        filters.offset === 0 ? newLogs : [...prev, ...newLogs]
      );
      setHasMore(newLogs.length === filters.count);
    } catch (err) {
      console.error('Errore fetching activities', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  /**
   * @description Infinite scroll
   */
  const handleScroll = () => {
    const el = containerRef.current;
    if (
      !el ||
      isLoading ||
      !hasMore ||
      el.scrollTop + el.clientHeight < el.scrollHeight - SCROLL_THRESHOLD
    )
      return;

    setFilters((prev) => ({
      ...prev,
      offset: prev.offset + prev.count,
    }));
  };

  /**
   * @description Fetch activities on offset change (and on any change of filters after the reset)
   */
  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  /**
   * @description Reset state and filters when file.uid changes (skip on first mount)
   */
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setActivities([]);
    setHasMore(true);
    setFilters({
      //userId: authenticatedUser?.userId,
      onBehalfOf: onBehalfOfUser?.userId,
      offset: 0,
      count: 25,
      fromDate: 0,
      toDate: dateToTicks(new Date()),
      detailsContains: '',
      //operationType: file.isFolder ? 'ReadFolder' : 'ReadFile',
      targetId: file.uid,
      targetType: file.isFolder ? 'Folder' : 'File',
    });
  }, [file.uid, onBehalfOfUser?.userId]);

  /**
   * @description Group activities by date and create items for Timeline
   */
  const timelineItems = useMemo(() => {
    const groups: Record<string, ActivityLog[]> = {};
    activities.forEach((act) => {
      const date = dateFromTicks(act.timestamp);
      const dateKey = dayjs(date).format('YYYY-MM-DD');
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(act);
    });

    const sortedDates = Object.keys(groups).sort(
      (a, b) => dayjs(b).valueOf() - dayjs(a).valueOf()
    );

    const items: any[] = [];
    sortedDates.forEach((dateKey) => {
      // header date
      items.push({
        key: `header-${dateKey}`,
        children: (
          <span className="w-full font-bold italic text-blue-dark">
            {dayjs(dateKey).format('DD MMMM YYYY')}
          </span>
        ),
        dot: <Icon name="CalendarClockIcon" className="w-6 text-blue-dark" />,
      });
      // activities of the day
      groups[dateKey].forEach((act, idx) => {
        const time = dayjs(dateFromTicks(act.timestamp)).format('HH:mm');
        items.push({
          key: `${dateKey}-${act.timestamp}-${idx}`,
          dot: (
            <Avatar size={26} className="bg-azure-hover">
              {act?.additionalData?.Name?.[0] ||
                act?.additionalData?.Surname?.[0] ||
                act?.additionalData?.Email?.[0] ||
                act?.additionalData?.Username?.[0]}
            </Avatar>
          ),
          children: (
            <div>
              <div className="flex flex-row items-start gap-2">
                <div className="flex flex-col">
                  <span className="font-medium">
                    {act?.additionalData?.Name} {act?.additionalData?.Surname}
                  </span>
                  {act?.additionalData?.Email && (
                    <span className="-mt-1 pl-0.5 text-xs text-gray-dark">
                      {act?.additionalData?.Email}
                    </span>
                  )}
                </div>
                <span className="pt-0.5 text-sm text-gray-dark">- {time}</span>
              </div>
              <div>{act.details}</div>
            </div>
          ),
        });
      });
    });

    return items;
  }, [activities]);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="h-full overflow-y-auto px-2.5 pt-2"
    >
      <Spin
        className="h-full pt-8"
        spinning={isLoading}
        tip="Loading activities..."
      >
        <Timeline items={timelineItems} />
      </Spin>
    </div>
  );
};

export default FileDetailActivitiesTab;
